import AppIconButton from "../form/AppIconButton";
import { useMemo, useState } from "react";
import { Box, Link, Stack, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppAutocomplete } from "../form";
import {
  AppDialog,
  AppDialogActions,
  AppDialogCancelButton,
  AppDialogContent,
  AppDialogPrimaryButton,
  AppDialogTitle,
} from "../dialog";
import type { DashboardDecorationPlacement } from "../../types/dashboard";
import type { DashboardQuery } from "../../types/query";
import { isQueryReferencedInDecorations } from "../../utils/resolveDecorationBindings";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";
import { useDeleteConfirm } from "../../hooks/useDeleteConfirm";

type QueryOption = {
  id: string;
  label: string;
  description?: string;
};

type Props = {
  assignedQueryIds: string[];
  queries: DashboardQuery[];
  decorations: DashboardDecorationPlacement[];
  onAssign: (queryId: string) => void | Promise<void>;
  onUnassign: (queryId: string) => void | Promise<void>;
};

export default function AssignedQueriesPanel({
  assignedQueryIds,
  queries,
  decorations,
  onAssign,
  onUnassign,
}: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const styles = custom.components.assignedQueriesPanel;
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingUnassign, setPendingUnassign] = useState<string | null>(null);

  const assignedSet = useMemo(() => new Set(assignedQueryIds), [assignedQueryIds]);
  const assignedQueries = useMemo(
    () => queries.filter((q) => q.id && assignedSet.has(q.id)),
    [queries, assignedSet]
  );
  const availableQueryOptions = useMemo<QueryOption[]>(
    () =>
      queries
        .filter((q) => q.id && !assignedSet.has(q.id))
        .map((q) => ({
          id: q.id!,
          label: q.name || q.id!,
          description: q.description,
        })),
    [queries, assignedSet]
  );

  const { confirmDelete, isCancelled } = useDeleteConfirm();

  const handleUnassign = async (queryId: string) => {
    if (isQueryReferencedInDecorations(queryId, decorations)) {
      setPendingUnassign(queryId);
      return;
    }
    const query = queries.find((q) => q.id === queryId);
    try {
      await confirmDelete({ itemName: query?.name || queryId });
      await onUnassign(queryId);
    } catch (error) {
      if (isCancelled(error)) return;
      throw error;
    }
  };

  const confirmUnassign = async () => {
    if (!pendingUnassign) return;
    await onUnassign(pendingUnassign);
    setPendingUnassign(null);
  };

  const handleAssign = (queryId: string) => {
    void Promise.resolve(onAssign(queryId)).then(() => setPickerOpen(false));
  };

  return (
    <Box sx={styles.root}>
      <Box sx={styles.assignBlock}>
        <Stack direction="row" sx={styles.headerRow}>
          <Typography variant="subtitle2" fontWeight={700}>
            {t("query.assign.title")}
          </Typography>
          <AppIconButton
            size="small"
            color="primary"
            title={t("query.assign.assign")}
            onClick={() => setPickerOpen((open) => !open)}
          >
            <AddIcon fontSize="small" />
          </AppIconButton>
        </Stack>

        {pickerOpen && (
          <Box sx={styles.pickerInline}>
            <AppAutocomplete<QueryOption>
              size="small"
              label={t("query.assign.searchPlaceholder")}
              options={availableQueryOptions}
              value={null}
              onChange={(_, option) => {
                if (!option) return;
                handleAssign(option.id);
              }}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              noOptionsText={t("query.assign.noAvailableQueries")}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Box sx={styles.itemText}>
                    <Typography variant="body2" sx={styles.pickerOptionLabel}>
                      {option.label}
                    </Typography>
                    {option.description && (
                      <Typography variant="caption" color="text.secondary" sx={styles.pickerOptionDescription}>
                        {option.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            />
          </Box>
        )}
      </Box>

      <Link component={RouterLink} to="/widgets?tab=queries" sx={styles.libraryLink}>
        {t("query.assign.goToLibrary")}
      </Link>

      {assignedQueries.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={styles.empty}>
          {t("query.assign.empty")}
        </Typography>
      ) : (
        <Box sx={styles.list}>
          {assignedQueries.map((query) => (
            <Box key={query.id} sx={styles.item}>
              <Box sx={styles.itemText}>
                <Typography variant="body2" sx={styles.itemTitle} noWrap>
                  {query.name || query.id}
                </Typography>
                {query.description && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {query.description}
                  </Typography>
                )}
              </Box>
              <Stack direction="row" sx={styles.itemActions}>
                <Tooltip title={t("query.assign.editQuery")}>
                  <AppIconButton
                    size="small"
                    busyIgnore
                    component={RouterLink}
                    to={`/dashboard-queries/${query.id}/edit`}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </AppIconButton>
                </Tooltip>
                <Tooltip title={t("query.assign.unassign")}>
                  <AppIconButton size="small" color="error" onClick={() => handleUnassign(query.id!)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </AppIconButton>
                </Tooltip>
              </Stack>
            </Box>
          ))}
        </Box>
      )}

      <AppDialog open={!!pendingUnassign} onClose={() => setPendingUnassign(null)} maxWidth="xs" fullWidth>
        <AppDialogTitle>{t("query.assign.unassign")}</AppDialogTitle>
        <AppDialogContent>
          <Typography variant="body2">{t("query.assign.inUseWarning")}</Typography>
        </AppDialogContent>
        <AppDialogActions>
          <AppDialogCancelButton onClick={() => setPendingUnassign(null)}>{t("common.cancel")}</AppDialogCancelButton>
          <AppDialogPrimaryButton onClick={() => confirmUnassign().catch(console.error)}>
            {t("query.assign.unassign")}
          </AppDialogPrimaryButton>
        </AppDialogActions>
      </AppDialog>
    </Box>
  );
}
